import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Label } from '../../components/ui/label';
import DatePicker from '../../components/DatePicker';

const AgeCalculator = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [birth, setBirth] = useState('2000-01-15');
  const [target, setTarget] = useState(today);

  const result = useMemo(() => {
    const b = new Date(birth);
    const t = new Date(target);
    if (isNaN(b) || isNaN(t) || b > t) return null;

    let years = t.getFullYear() - b.getFullYear();
    let months = t.getMonth() - b.getMonth();
    let days = t.getDate() - b.getDate();
    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(t.getFullYear(), t.getMonth(), 0).getDate();
      days += prevMonth;
    }
    if (months < 0) { years -= 1; months += 12; }

    const msPerDay = 1000 * 60 * 60 * 24;
    const totalDays = Math.floor((t - b) / msPerDay);
    const totalMonths = years * 12 + months;
    const totalHours = totalDays * 24;
    const totalMinutes = totalHours * 60;

    // next birthday
    const nextBday = new Date(t.getFullYear(), b.getMonth(), b.getDate());
    if (nextBday <= t) nextBday.setFullYear(t.getFullYear() + 1);
    const daysToNext = Math.ceil((nextBday - t) / msPerDay);

    return { years, months, days, totalDays, totalMonths, totalHours, totalMinutes, nextBday, daysToNext };
  }, [birth, target]);

  return (
    <CalculatorLayout
      title="Age Calculator"
      subtitle="Calculate your exact age in years, months, days — and days until your next birthday."
      category="Other"
      description={<p>This age calculator computes the exact duration between two dates — typically your date of birth and today. Use it to know your age for official forms, plan birthdays, or track milestones.</p>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Date of Birth</Label>
            <DatePicker value={birth} onChange={setBirth} minYear={1900} maxYear={new Date().getFullYear()} />
            <p className="text-[11px] text-slate-400 mt-1">Type (DD/MM/YYYY) or pick from calendar</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Calculate Age At</Label>
            <DatePicker value={target} onChange={setTarget} minYear={1900} maxYear={new Date().getFullYear() + 50} />
          </div>
          {result && result.daysToNext > 0 && (
            <div className="p-4 bg-indgreen-50 rounded-lg border border-indgreen-200">
              <div className="text-xs font-semibold uppercase text-indgreen-700 mb-0.5">Next Birthday</div>
              <div className="text-sm text-slate-700">
                In <span className="font-bold text-indgreen-700 display-font">{result.daysToNext}</span> day{result.daysToNext !== 1 ? 's' : ''} — {result.nextBday.toDateString()}
              </div>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-white rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Your Age</div>
          {result ? (
            <>
              <div className="mt-2 flex items-baseline gap-1 flex-wrap">
                <span className="font-display text-5xl font-bold text-bharat-navy display-font">{result.years}</span>
                <span className="text-sm text-slate-500 mr-2">yrs</span>
                <span className="font-display text-3xl font-bold text-saffron-600 display-font">{result.months}</span>
                <span className="text-sm text-slate-500 mr-2">mo</span>
                <span className="font-display text-3xl font-bold text-indgreen-600 display-font">{result.days}</span>
                <span className="text-sm text-slate-500">days</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-5 text-xs">
                <ResBox label="Total Months" value={result.totalMonths.toLocaleString()} />
                <ResBox label="Total Days" value={result.totalDays.toLocaleString()} />
                <ResBox label="Total Hours" value={result.totalHours.toLocaleString()} />
                <ResBox label="Total Minutes" value={result.totalMinutes.toLocaleString()} />
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500 mt-3">Please select a valid date of birth before the target date.</p>
          )}
        </div>
      </div>
    </CalculatorLayout>
  );
};

const ResBox = ({ label, value }) => (
  <div className="bg-white rounded-md p-2.5 border border-slate-100">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="display-font font-semibold text-bharat-navy mt-0.5">{value}</div>
  </div>
);

export default AgeCalculator;
